import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { submitProject, getSettings } from '../api/projects';
import t from '../i18n/fr.json';
import '../styles/Submit.css';
import '../styles/ICOListing.css';
import { PROJECT_CATEGORIES, isValidYouTubeUrl } from '../constants/categories';

import planete_ico from '../images/logo_onlineico_v.png';
import planete_ido from '../images/planete_ido.png';
import Navbar from '../components/Navbar';

const STEPS = ['Info', 'Details', 'Tokenomics', 'Team', 'Socials & Ads'];
const DISTRIB_COLORS = ['#F59E0B', '#34D399', '#3B82F6', '#8B5CF6', '#EC4899', '#10B981', '#F97316', '#06B6D4', '#A855F7', '#EF4444'];

export default function Submit() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [settings, setSettings] = useState(null);

  const [form, setForm] = useState({
    name: '', email: '', ticker: '', type: 'ico', chain: '', categorie: '',
    website: '', twitter: '', telegram: '', discord: '', whitepaper: '', logo_url: '',
    description: '', url_video: '', contract_address: '', launch_date: '', presale_start: '', presale_end: '',
    kyc: '', kyc_url: '', audit: '', audit_url: '',
    tokenomics: { 'Supply total': '', 'Hard Cap': '', 'Soft Cap': '', 'Vesting': '', devise: '', phases: [] },
    token_distribution: [],
    roadmap: [],
    team: [],
    faq: [],
    promoted_package: '',
    promoted_duration: '',
  });

  useEffect(() => {
    getSettings().then(s => setSettings(s));
  }, []);

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));
  const updateTokenomics = (field, value) => setForm(prev => ({ ...prev, tokenomics: { ...prev.tokenomics, [field]: value } }));

  const addPhase = () => setForm(prev => ({ ...prev, tokenomics: { ...prev.tokenomics, phases: [...prev.tokenomics.phases, { price: '', duration: '' }] } }));
  const updatePhase = (i, field, value) => { const phases = [...form.tokenomics.phases]; phases[i] = { ...phases[i], [field]: value }; setForm(prev => ({ ...prev, tokenomics: { ...prev.tokenomics, phases } })); };
  const removePhase = (i) => { const phases = form.tokenomics.phases.filter((_, idx) => idx !== i); setForm(prev => ({ ...prev, tokenomics: { ...prev.tokenomics, phases } })); };

  const addDistrib = () => update('token_distribution', [...form.token_distribution, { label: '', percent: '', color: DISTRIB_COLORS[form.token_distribution.length % DISTRIB_COLORS.length] }]);
  const updateDistrib = (i, field, value) => { const d = [...form.token_distribution]; d[i] = { ...d[i], [field]: value }; update('token_distribution', d); };
  const removeDistrib = (i) => update('token_distribution', form.token_distribution.filter((_, idx) => idx !== i));

  const addRoadmap = () => update('roadmap', [...form.roadmap, '']);
  const updateRoadmap = (i, value) => { const r = [...form.roadmap]; r[i] = value; update('roadmap', r); };
  const removeRoadmap = (i) => update('roadmap', form.roadmap.filter((_, idx) => idx !== i));

  const addTeam = () => update('team', [...form.team, { name: '', role: '', bio: '' }]);
  const updateTeam = (i, field, value) => { const team = [...form.team]; team[i] = { ...team[i], [field]: value }; update('team', team); };
  const removeTeam = (i) => update('team', form.team.filter((_, idx) => idx !== i));

  const addFaq = () => update('faq', [...form.faq, { q: '', a: '' }]);
  const updateFaq = (i, field, value) => { const faq = [...form.faq]; faq[i] = { ...faq[i], [field]: value }; update('faq', faq); };
  const removeFaq = (i) => update('faq', form.faq.filter((_, idx) => idx !== i));

  const validateStep = () => {
    const fullUrlRegex = /^https?:\/\/[^\s/$.?#].[^\s]*$/i;
    const emailRegex = /^[^\s@]+@([^\s@]+\.)+[^\s@]+$/;
    const twitterRegex = /^(https?:\/\/)?(www\.)?(twitter\.com|x\.com)\/[A-Za-z0-9_]{1,15}(\/)?$/;
    const numberRegex = /^\d+(\.\d+)?$/;

    if (step === 0) {
      if (!form.name || !form.email || !form.ticker || !form.chain || !form.categorie || !form.website || !form.whitepaper || !form.type)
        return 'Please fill in all required fields.';
      if (!PROJECT_CATEGORIES.includes(form.categorie)) return 'Invalid category.';
      if (form.name.trim().length < 2) return 'Project name too short (min 2 characters).';
      if (form.ticker.trim().length < 2) return 'Ticker too short (min 2 characters).';
      if (/\s/.test(form.ticker)) return 'The ticker must not contain spaces.';
      if (!emailRegex.test(form.email)) return 'Invalid email.';
      if (!fullUrlRegex.test(form.website)) return 'Invalid website URL (must start with http:// or https://).';
      if (!fullUrlRegex.test(form.whitepaper)) return 'Invalid whitepaper URL.';
      if (form.logo_url) {
        if (!fullUrlRegex.test(form.logo_url)) return 'Invalid logo URL.';
        const logoExt = form.logo_url.split('?')[0].match(/\.(jpg|jpeg|png|webp|svg)$/i);
        if (!logoExt) return 'The logo must be an image (jpg, png, webp, svg).';
        try {
          const logoDomain    = new URL(form.logo_url).hostname.replace(/^www\./, '');
          const websiteDomain = new URL(form.website).hostname.replace(/^www\./, '');
          if (logoDomain !== websiteDomain) return 'Your logo must be hosted on your own domain (' + websiteDomain + '). External image hosts are not allowed.';
        } catch (e) {
          return 'Invalid logo URL.';
        }
      }
    }

    if (step === 1) {
      if (!form.description || !form.contract_address) return 'Description and contract address are required.';
      if (form.description.length > 5000) return 'Description cannot exceed 5000 characters.';
      if (form.description.trim().length < 20) return 'Description too short (min 20 characters).';
      if (form.url_video && !isValidYouTubeUrl(form.url_video)) return 'Video URL must be a valid YouTube link (youtube.com or youtu.be).';
      if (form.kyc_url && !fullUrlRegex.test(form.kyc_url)) return 'Invalid KYC certificate URL.';
      if (form.audit_url && !fullUrlRegex.test(form.audit_url)) return 'Invalid audit report URL.';
      if (form.launch_date && isNaN(Date.parse(form.launch_date))) return 'Invalid launch date.';
      if (form.presale_start && isNaN(Date.parse(form.presale_start))) return 'Invalid presale start date.';
      if (form.presale_end && isNaN(Date.parse(form.presale_end))) return 'Invalid presale end date.';
      if (form.presale_start && form.presale_end && new Date(form.presale_start) >= new Date(form.presale_end))
        return 'Presale end date must be after the start date.';
    }

    if (step === 2) {
      const { tokenomics, token_distribution, roadmap } = form;
      if (!tokenomics['Supply total'] || !tokenomics['Hard Cap'] || !tokenomics['Vesting'] || !tokenomics.devise)
        return 'Total supply, Hard Cap, Vesting and Currency are required.';
      if (tokenomics.devise.trim().length < 3) return 'Currency must be at least 3 characters.';
      if (!/^[A-Za-z0-9]{3,}$/.test(tokenomics.devise.trim())) return 'Currency must contain only letters/numbers (min 3).';
      if (!numberRegex.test(tokenomics['Supply total'].toString().replace(/,/g, ''))) return 'Total supply must be a valid number.';
      if (!numberRegex.test(tokenomics['Hard Cap'].toString().replace(/,/g, ''))) return 'Hard Cap must be a valid number.';
      if (tokenomics['Soft Cap'] && !numberRegex.test(tokenomics['Soft Cap'].toString().replace(/,/g, ''))) return 'Soft Cap must be a valid number.';
      const hardCap = parseFloat(tokenomics['Hard Cap'].toString().replace(/,/g, ''));
      const softCap = tokenomics['Soft Cap'] ? parseFloat(tokenomics['Soft Cap'].toString().replace(/,/g, '')) : 0;
      if (softCap > hardCap) return 'Hard Cap cannot be lower than Soft Cap.';
      if (tokenomics.phases.length === 0) return 'Add at least one token price.';
      for (let i = 0; i < tokenomics.phases.length; i++) {
        const phase = tokenomics.phases[i];
        if (!phase.price) return `Phase ${i + 1}: price is required.`;
        const priceStr = phase.price.toString().replace(/,/g, '.');
        if (isNaN(parseFloat(priceStr)) || parseFloat(priceStr) <= 0) return `Phase ${i + 1}: price must be a positive number.`;
      }
      let totalPercent = 0;
      for (let i = 0; i < token_distribution.length; i++) {
        const d = token_distribution[i];
        if (d.percent) {
          const p = parseFloat(d.percent.toString().replace(/,/g, '.'));
          if (isNaN(p) || p < 0) return `Distribution row ${i + 1}: invalid percentage.`;
          totalPercent += p;
        }
      }
      if (totalPercent > 100) return 'The sum of distribution percentages exceeds 100%.';
      for (let i = 0; i < roadmap.length; i++) {
        if (roadmap[i] && roadmap[i].trim().length < 3) return `Roadmap step ${i + 1} too short.`;
      }
    }

    if (step === 3) {
      for (let i = 0; i < form.team.length; i++) {
        if (form.team[i].name && form.team[i].name.trim().length < 2) return `Member ${i + 1} name too short.`;
      }
    }

    if (step === 4) {
      if (!form.twitter) return 'Twitter link is required.';
      if (!twitterRegex.test(form.twitter)) return 'Invalid Twitter link (must be twitter.com or x.com).';
      if (form.telegram && !fullUrlRegex.test(form.telegram)) return 'Invalid Telegram URL.';
      if (form.discord && !fullUrlRegex.test(form.discord)) return 'Invalid Discord URL.';
      if (form.promoted_package && !form.promoted_duration) return 'Please select a duration for the Promoted package.';
    }

    return '';
  };

  const handleNext = () => {
    const err = validateStep();
    if (err) { setError(err); return; }
    setError('');
    setStep(s => s + 1);
  };

  const handleSubmit = async () => {
    const err = validateStep();
    if (err) { setError(err); return; }
    setLoading(true);
    setError('');
    try {
      const payload = { ...form, soft_cap: form.tokenomics['Soft Cap'], devise: form.tokenomics.devise };
      const result = await submitProject(payload);
      if (result.success) setSubmitted(true);
      else setError(result.error || 'Submission error');
    } catch (e) {
      setError('Network error, please try again.');
    }
    setLoading(false);
  };

  if (submitted) {
    return (
      <div className="app-wrap">
        <nav className="nav">
        {/* GAUCHE — Logo */}
            <div className="nav-logo" style={{ cursor: 'pointer', flexShrink: 0 }} onClick={() => navigate('/')}>
                ONLINEICO
            </div>
        </nav>
        <div className="submit-wrap">
          <div className="submit-card">
            <div className="success-wrap">
              <div className="success-icon">🎉</div>
              <div className="success-title">Project submitted!</div>
              <p className="success-text">
                A verification email has been sent to you.<br />
                Click the link in the email to confirm your address.<br /><br />
                After verification, our team will review your project before publication.
              </p>
              <button className="btn-submit" style={{ marginTop: '2rem' }} onClick={() => navigate('/')}>Back to listings</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-wrap">
<Helmet>
    <title>List Your Project Crypto ICO & IDO | OnlineICO</title>
    <meta name="description" content="List your crypto project on OnlineICO. Submit your ICO or IDO for free and reach thousands of investors. Promoted listing options available." />
    <link rel="canonical" href="https://onlineico.com/add-crypto-project-ico" />

    <meta property="og:type" content="website" />
    <meta property="og:site_name" content="OnlineICO" />
    <meta property="og:title" content="List Your Project Crypto ICO / IDO | OnlineICO" />
    <meta property="og:description" content="List your crypto project on OnlineICO. Submit your ICO or IDO for free and reach thousands of investors." />
    <meta property="og:url" content="https://onlineico.com/add-crypto-project-ico" />
    <meta property="og:image" content="https://onlineico.com/og-image.png" />
    <meta property="og:image:width" content="1200" />
    <meta property="og:image:height" content="630" />

    <meta name="twitter:card" content="summary_large_image" />
    <meta name="twitter:site" content="@online_ico" />
    <meta name="twitter:title" content="List Your Project Crypto ICO / IDO | OnlineICO" />
    <meta name="twitter:description" content="List your crypto project on OnlineICO. Submit your ICO or IDO for free and reach thousands of investors." />
    <meta name="twitter:image" content="https://onlineico.com/og-image.png" />

    <script type="application/ld+json">{JSON.stringify({
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": "List Your Project Crypto ICO / IDO | OnlineICO",
        "description": "List your crypto project on OnlineICO. Submit your ICO or IDO for free and reach thousands of investors.",
        "url": "https://onlineico.com/add-crypto-project-ico",
        "publisher": {
            "@type": "Organization",
            "name": "OnlineICO",
            "url": "https://onlineico.com",
            "logo": "https://onlineico.com/logo_onlineico_v.png"
        }
    })}</script>
</Helmet>


<Navbar />

      <div className="hero" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '2rem', flexWrap: 'wrap' }}>
        <img src={planete_ico} alt="ico" style={{ transition: 'transform 0.3s ease', width: '120px' }} onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.02)'} onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'} />
        <div>
          <div className="hero-title"><span className="accent">✦ {t.hero_badge} ✦</span></div>
          <p className="hero-sub">{t.hero_sub}<br /><span className="accent" style={{ background: "linear-gradient(135deg, #fff 0%, #FF8C00 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>{t.hero_sub2}</span></p>
        </div>
        <img src={planete_ido} alt="ido" style={{ transition: 'transform 0.3s ease', width: '120px' }} onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.02)'} onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'} />
      </div>

      <div className="submit-wrap">
        <div className="progress-steps">
          {STEPS.map((s, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center' }}>
              <div className="step-item">
                <div className={`step-circle ${i < step ? 'done' : i === step ? 'active' : 'todo'}`}>{i < step ? '✓' : i + 1}</div>
                <div className={`step-label ${i < step ? 'done' : i === step ? 'active' : ''}`}>{s}</div>
              </div>
              {i < STEPS.length - 1 && <div className={`step-line ${i < step ? 'done' : ''}`} />}
            </div>
          ))}
        </div>

        {/* ÉTAPE 0 — INFOS */}
        {step === 0 && (
          <div className="submit-card">
            <div className="submit-card-title"><span>◉</span> General information</div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Project name <span>*</span></label>
                <input className="form-input" placeholder="Ex: NovaBurn" value={form.name} onChange={e => update('name', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Ticker <span>*</span></label>
                <input className="form-input" placeholder="Ex: $NBX" value={form.ticker} onChange={e => update('ticker', e.target.value)} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Type <span>*</span></label>

                <select className="form-select" value={form.type} onChange={e => update('type', e.target.value)}>
                  <option value=""></option>
                  <option value="ico">ICO</option>
                  <option value="ido">IDO</option>
                  <option value="nft">NFT</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Blockchain <span>*</span></label>
                <input className="form-input" placeholder="Ex: BNB Chain, Solana..." value={form.chain} onChange={e => update('chain', e.target.value)} />
              </div>
              

            </div>
            <div className="form-row">
            
              <div className="form-group">
                <label className="form-label">Category <span>*</span></label>
                <select className="form-select" value={form.categorie} onChange={e => update('categorie', e.target.value)}>
                  <option value="">— Select —</option>
                  {PROJECT_CATEGORIES.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Website <span>*</span></label>
                <input className="form-input" placeholder="https://..." value={form.website} onChange={e => update('website', e.target.value)} />
              </div>

            </div>
            <div className="form-row">
             
              <div className="form-group">
                <label className="form-label">Whitepaper <span>*</span></label>
                <input className="form-input" placeholder="https://..." value={form.whitepaper} onChange={e => update('whitepaper', e.target.value)} />
              </div>

              <div className="form-group">
                <label className="form-label">Logo URL <span style={{ color: '#64748b', fontWeight: 400 }}>(Must be hosted on your own domain.)</span></label>
                <input className="form-input" placeholder="https://yourproject.com/logo.png" value={form.logo_url} onChange={e => update('logo_url', e.target.value)} />

                {form.logo_url && form.website && (() => {
                  try {
                    const logoDomain    = new URL(form.logo_url).hostname.replace(/^www\./, '');
                    const websiteDomain = new URL(form.website).hostname.replace(/^www\./, '');
                    if (logoDomain !== websiteDomain) return (
                      <div style={{ marginTop: '4px', fontSize: '0.75rem', color: '#f87171' }}>
                        ❌ Logo domain <strong>{logoDomain}</strong> does not match your website domain <strong>{websiteDomain}</strong>.
                      </div>
                    );
                    return <div style={{ marginTop: '4px', fontSize: '0.75rem', color: '#34D399' }}>✓ Domain verified</div>;
                  } catch { return null; }
                })()}
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Contact email <span>*</span></label>
              <input className="form-input" type="email" placeholder="your@email.com" value={form.email} onChange={e => update('email', e.target.value)} />
            </div>
          </div>
        )}

        {/* ÉTAPE 1 — DÉTAILS */}
        {step === 1 && (
          <div className="submit-card">
            <div className="submit-card-title"><span>◉</span> Project details</div>
            <div className="form-group">
              <label className="form-label">Description <span>*</span></label>
              <textarea className="form-textarea" placeholder="Describe your project..." value={form.description} onChange={e => update('description', e.target.value)} rows={5} />
            </div>
            <div className="form-group">
              <label className="form-label">YouTube video URL <span style={{ color: '#64748b' }}>(optional)</span></label>
              <input className="form-input" placeholder="https://www.youtube.com/watch?v=..." value={form.url_video} onChange={e => update('url_video', e.target.value)} />
              <div style={{ color: '#64748b', fontSize: '0.75rem', marginTop: '6px' }}>The video will be displayed at the top of your project page description.</div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Contract Address <span>*</span></label>
                <input className="form-input" placeholder="0x..." value={form.contract_address} onChange={e => update('contract_address', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Launch date</label>
                <input className="form-input" type="date" value={form.launch_date} onChange={e => update('launch_date', e.target.value)} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Presale start</label>
                <input className="form-input" type="date" value={form.presale_start} onChange={e => update('presale_start', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Presale end</label>
                <input className="form-input" type="date" value={form.presale_end} onChange={e => update('presale_end', e.target.value)} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">KYC — Company</label>
                <input className="form-input" placeholder="Ex: CertiK" value={form.kyc} onChange={e => update('kyc', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">KYC — Certificate URL</label>
                <input className="form-input" placeholder="https://..." value={form.kyc_url} onChange={e => update('kyc_url', e.target.value)} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Audit — Company</label>
                <input className="form-input" placeholder="Ex: SpyWolf" value={form.audit} onChange={e => update('audit', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Audit — Report URL</label>
                <input className="form-input" placeholder="https://..." value={form.audit_url} onChange={e => update('audit_url', e.target.value)} />
              </div>
            </div>
          </div>
        )}

        {/* ÉTAPE 2 — TOKENOMICS */}
        {step === 2 && (
          <div className="submit-card">
            <div className="submit-card-title"><span>◉</span> Tokenomics</div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Supply total <span>*</span></label>
                <input className="form-input" placeholder="Ex: 1 000 000 000" value={form.tokenomics['Supply total']} onChange={e => updateTokenomics('Supply total', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Hard Cap <span>*</span></label>
                <input className="form-input" placeholder="Ex: 2 000 000" value={form.tokenomics['Hard Cap']} onChange={e => updateTokenomics('Hard Cap', e.target.value)} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Soft Cap</label>
                <input className="form-input" placeholder="Ex: 500 000" value={form.tokenomics['Soft Cap']} onChange={e => updateTokenomics('Soft Cap', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Currency <span>*</span></label>
                <input className="form-input" placeholder="Ex: USDT, USDC, BNB..." value={form.tokenomics.devise} onChange={e => updateTokenomics('devise', e.target.value)} />
              </div>
            </div>
            <div className="form-row single">
              <div className="form-group">
                <label className="form-label">Vesting <span>*</span></label>
                <input className="form-input" placeholder="Ex: Cliff 6 months, linear vesting 12 months" value={form.tokenomics['Vesting']} onChange={e => updateTokenomics('Vesting', e.target.value)} />
              </div>
            </div>
            <div style={{ marginBottom: '24px' }}>
              <div style={{ color: '#94a3b8', fontSize: '0.82rem', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '12px' }}>Token price <span style={{ color: '#EC4899' }}>*</span></div>
              {form.tokenomics.phases.map((phase, i) => (
                <div className="dynamic-item" key={i} style={{ marginBottom: '16px' }}>
                  <button className="dynamic-item-remove" onClick={() => removePhase(i)}>✕</button>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Prix <span>*</span></label>
                      <input className="form-input" placeholder="Ex: 0.002" value={phase.price} onChange={e => updatePhase(i, 'price', e.target.value)} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Duration / Quantity <span style={{ color: '#64748b' }}>(optional)</span></label>
                      <input className="form-input" placeholder="Ex: 30 days / 500 000 tokens" value={phase.duration} onChange={e => updatePhase(i, 'duration', e.target.value)} />
                    </div>
                  </div>
                </div>
              ))}
              <button className="add-btn" onClick={addPhase}>+ Add a price</button>
            </div>
            <div style={{ marginBottom: '24px' }}>
              <div style={{ color: '#94a3b8', fontSize: '0.82rem', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '12px' }}>Token distribution</div>
              {form.token_distribution.map((d, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
                  <div style={{ width: '12px', height: '12px', borderRadius: '3px', background: DISTRIB_COLORS[i % DISTRIB_COLORS.length], flexShrink: 0 }} />
                  <input className="form-input" placeholder="Ex: Presale" value={d.label} onChange={e => updateDistrib(i, 'label', e.target.value)} style={{ flex: 2 }} />
                  <input className="form-input" placeholder="%" type="number" value={d.percent} onChange={e => updateDistrib(i, 'percent', e.target.value)} style={{ flex: 1 }} />
                  <button onClick={() => removeDistrib(i)} style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: '1rem', padding: '4px' }}>✕</button>
                </div>
              ))}
              <button className="add-btn" onClick={addDistrib}>+ Add a line</button>
            </div>
            <div>
              <div style={{ color: '#94a3b8', fontSize: '0.82rem', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '12px' }}>Roadmap</div>
              {form.roadmap.map((item, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                  <input className="form-input" placeholder="Ex: Q1 2025: ICO Launch" value={item} onChange={e => updateRoadmap(i, e.target.value)} style={{ flex: 1 }} />
                  <button onClick={() => removeRoadmap(i)} style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: '1rem', padding: '4px', flexShrink: 0 }}>✕</button>
                </div>
              ))}
              <button className="add-btn" onClick={addRoadmap}>+ Add a step</button>
            </div>
          </div>
        )}

        {/* ÉTAPE 3 — ÉQUIPE & FAQ */}
        {step === 3 && (
          <div className="submit-card">
            <div className="submit-card-title"><span>◉</span> Team <span style={{ color: '#64748b', fontSize: '0.8rem', fontWeight: '400' }}>(optional)</span></div>
            {form.team.map((member, i) => (
              <div className="dynamic-item" key={i} style={{ marginBottom: '16px' }}>
                <button className="dynamic-item-remove" onClick={() => removeTeam(i)}>✕</button>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Name</label>
                    <input className="form-input" placeholder="Ex: Alex Martin" value={member.name} onChange={e => updateTeam(i, 'name', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Role</label>
                    <input className="form-input" placeholder="Ex: CEO & Founder" value={member.role} onChange={e => updateTeam(i, 'role', e.target.value)} />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Bio</label>
                  <textarea className="form-textarea" placeholder="Short biography..." value={member.bio} onChange={e => updateTeam(i, 'bio', e.target.value)} rows={2} />
                </div>
              </div>
            ))}
            <button className="add-btn" onClick={addTeam}>+ Add a member</button>
            <div style={{ marginTop: '32px' }}>
              <div className="submit-card-title"><span>◉</span> FAQ <span style={{ color: '#64748b', fontSize: '0.8rem', fontWeight: '400' }}>(optional)</span></div>
              {form.faq.map((item, i) => (
                <div className="dynamic-item" key={i} style={{ marginBottom: '16px' }}>
                  <button className="dynamic-item-remove" onClick={() => removeFaq(i)}>✕</button>
                  <div className="form-group">
                    <label className="form-label">Question</label>
                    <input className="form-input" placeholder="Ex: How to buy?" value={item.q} onChange={e => updateFaq(i, 'q', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Answer</label>
                    <textarea className="form-textarea" placeholder="Answer..." value={item.a} onChange={e => updateFaq(i, 'a', e.target.value)} rows={2} />
                  </div>
                </div>
              ))}
              <button className="add-btn" onClick={addFaq}>+ Add a question</button>
            </div>
          </div>
        )}

        {/* ÉTAPE 4 — RÉSEAUX & PUB */}
        {step === 4 && (
          <div className="submit-card">
            <div className="submit-card-title"><span>◉</span> Social networks</div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Twitter <span>*</span></label>
                <input className="form-input" placeholder="https://twitter.com/..." value={form.twitter} onChange={e => update('twitter', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Telegram</label>
                <input className="form-input" placeholder="https://t.me/..." value={form.telegram} onChange={e => update('telegram', e.target.value)} />
              </div>
            </div>
            <div className="form-row single">
              <div className="form-group">
                <label className="form-label">Discord</label>
                <input className="form-input" placeholder="https://discord.gg/..." value={form.discord} onChange={e => update('discord', e.target.value)} />
              </div>
            </div>

            <div className="submit-card-title" style={{ marginTop: '24px' }}><span>◉</span> Promoted option <span style={{ color: '#64748b', fontSize: '0.8rem', fontWeight: '400' }}>(optional)</span></div>

            {settings && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '20px' }}>
                {[
                  { key: 'boost', icon: '⚡', label: 'Boost', desc: 'Give your project a push! A boost temporarily improves your ranking position for 48h. The more you boost, the higher you climb.', price: `from ${settings.boost_single} $` },
                  { key: 'banner', icon: '📢', label: 'Banner', desc: 'Your advertising banner (300x250px image) appears in the right column of other projects\' detail pages. Perfect for targeting investors already interested in similar projects.', price: `from ${settings.banner_week} $` },
                  { key: 'premium', icon: '⭐', label: 'Premium', desc: 'Your project is displayed first in the "Featured" section on the homepage. Maximum visibility — it\'s the first thing visitors see when they arrive on the site.', price: `from ${settings.premium_week} $` },
                ].map(pkg => (
                  <div key={pkg.key}
                    onClick={() => { update('promoted_package', pkg.key); update('promoted_duration', ''); }}
                    style={{
                      background: form.promoted_package === pkg.key ? 'rgba(245,158,11,0.08)' : 'rgba(255,255,255,0.02)',
                      border: `1px solid ${form.promoted_package === pkg.key ? 'rgba(245,158,11,0.4)' : 'rgba(255,255,255,0.07)'}`,
                      borderRadius: '16px', padding: '16px', cursor: 'pointer', transition: 'all 0.2s',
                      display: 'flex', gap: '16px', alignItems: 'flex-start'
                    }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(245,158,11,0.3)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = form.promoted_package === pkg.key ? 'rgba(245,158,11,0.4)' : 'rgba(255,255,255,0.07)'; }}
                  >
                    <div style={{ fontSize: '1.8rem', flexShrink: 0 }}>{pkg.icon}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                        <div style={{ color: '#F1F5F9', fontFamily: 'Syne, sans-serif', fontWeight: '700', fontSize: '1rem' }}>{pkg.label}</div>
                        <div style={{ color: '#F59E0B', fontWeight: '600', fontSize: '0.85rem' }}>{pkg.price}</div>
                      </div>
                      <div style={{ color: '#94a3b8', fontSize: '0.82rem', lineHeight: '1.6' }}>{pkg.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {form.promoted_package && settings && (
              <>
                <div style={{ color: '#94a3b8', fontSize: '0.82rem', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '12px' }}>Duration</div>
                <div className="duration-options">
                  {form.promoted_package === 'boost' && (
                    <>
                      <button className={`duration-btn${form.promoted_duration === 'boost_single' ? ' selected' : ''}`} onClick={() => update('promoted_duration', 'boost_single')}>x1 — {settings.boost_single} $</button>
                      <button className={`duration-btn${form.promoted_duration === 'boost_pack10' ? ' selected' : ''}`} onClick={() => update('promoted_duration', 'boost_pack10')}>x10 — {settings.boost_pack10} $</button>
                    </>
                  )}
                  {form.promoted_package === 'banner' && (
                    <>
                      <button className={`duration-btn${form.promoted_duration === 'banner_week' ? ' selected' : ''}`} onClick={() => update('promoted_duration', 'banner_week')}>1 week — {settings.banner_week} $</button>
                      <button className={`duration-btn${form.promoted_duration === 'banner_month' ? ' selected' : ''}`} onClick={() => update('promoted_duration', 'banner_month')}>1 month — {settings.banner_month} $</button>
                    </>
                  )}
                  {form.promoted_package === 'premium' && (
                    <>
                      <button className={`duration-btn${form.promoted_duration === 'premium_week' ? ' selected' : ''}`} onClick={() => update('promoted_duration', 'premium_week')}>1 week — {settings.premium_week} $</button>
                      <button className={`duration-btn${form.promoted_duration === 'premium_month' ? ' selected' : ''}`} onClick={() => update('promoted_duration', 'premium_month')}>1 month — {settings.premium_month} $</button>
                    </>
                  )}
                </div>
                {form.promoted_duration && (
                  <div style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: '16px', padding: '16px', textAlign: 'center', marginTop: '16px' }}>
                    <div style={{ color: '#94a3b8', fontSize: '0.82rem', marginBottom: '6px' }}>Total</div>
                    <div style={{ color: '#F59E0B', fontFamily: 'Syne, sans-serif', fontWeight: '700', fontSize: '1.8rem' }}>{settings[form.promoted_duration]} $</div>
                    <div style={{ color: '#94a3b8', fontSize: '0.78rem', marginTop: '8px' }}>Payment after submission via your project page</div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {error && (
          <div style={{ color: '#f87171', fontSize: '0.85rem', textAlign: 'center', marginBottom: '12px' }}>{error}</div>
        )}

        <div className="nav-buttons">
          {step > 0 ? (
            <button className="btn-prev" onClick={() => { setError(''); setStep(s => s - 1); }}>← Previous</button>
          ) : (
            <button className="btn-prev" onClick={() => navigate('/')}>← Back</button>
          )}
          {step < STEPS.length - 1 ? (
            <button className="btn-next" onClick={handleNext}>Next →</button>
          ) : (
            <button className="btn-submit" onClick={handleSubmit} disabled={loading}>
              {loading ? 'Sending...' : '🚀 Submit project'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}